import { Directive, Input, HostBinding } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Directive({
    selector: '[euiBase]',
    host: {
        '[attr.aria-disabled]': 'this._euiDisabled.toString()',
    },
})
export class BaseDirective {

    @HostBinding('attr.data-e2e') @Input() e2eAttr;
    @Input() styleClass: string;

    // COLORS
    @Input()
    get euiPrimary() { return this._euiPrimary; }
    set euiPrimary(value) { this._euiPrimary = coerceBooleanProperty(value); }
    _euiPrimary = false;

    @Input()
    get euiSecondary() { return this._euiSecondary; }
    set euiSecondary(value) { this._euiSecondary = coerceBooleanProperty(value); }
    _euiSecondary = false;

    @Input()
    get euiInfo() { return this._euiInfo; }
    set euiInfo(value) { this._euiInfo = coerceBooleanProperty(value); }
    _euiInfo = false;

    @Input()
    get euiSuccess() { return this._euiSuccess; }
    set euiSuccess(value) { this._euiSuccess = coerceBooleanProperty(value); }
    _euiSuccess = false;

    @Input()
    get euiWarning() { return this._euiWarning; }
    set euiWarning(value) { this._euiWarning = coerceBooleanProperty(value); }
    _euiWarning = false;

    @Input()
    get euiDanger() { return this._euiDanger; }
    set euiDanger(value) { this._euiDanger = coerceBooleanProperty(value); }
    _euiDanger = false;

    @Input()
    get euiAccent() { return this._euiAccent; }
    set euiAccent(value) { this._euiAccent = coerceBooleanProperty(value); }
    _euiAccent = false;

    @Input()
    get color() { return this._color; }
    set color(value: string) {
        this._euiPrimary = (value === 'primary');
        this._euiSecondary = (value === 'secondary');
        this._euiWarning = (value === 'warning');
        this._euiInfo = (value === 'info');
        this._euiSuccess = (value === 'success');
        this._euiDanger = (value === 'danger');
        this._euiAccent = (value === 'accent');
        this._color = value;
    }
    _color = '';

    // SIZES
    @Input()
    get euiSize2XS() { return this._euiSize2XS; }
    set euiSize2XS(value) { this._euiSize2XS = coerceBooleanProperty(value); }
    _euiSize2XS = false;

    @Input()
    get euiSizeXS() { return this._euiSizeXS; }
    set euiSizeXS(value) { this._euiSizeXS = coerceBooleanProperty(value); }
    _euiSizeXS = false;

    @Input()
    get euiSizeS() { return this._euiSizeS; }
    set euiSizeS(value) { this._euiSizeS = coerceBooleanProperty(value); }
    _euiSizeS = false;

    @Input()
    get euiSizeM() { return this._euiSizeM; }
    set euiSizeM(value) { this._euiSizeM = coerceBooleanProperty(value); }
    _euiSizeM = false;

    @Input()
    get euiSizeL() { return this._euiSizeL; }
    set euiSizeL(value) { this._euiSizeL = coerceBooleanProperty(value); }
    _euiSizeL = false;

    @Input()
    get euiSizeXL() { return this._euiSizeXL; }
    set euiSizeXL(value) { this._euiSizeXL = coerceBooleanProperty(value); }
    _euiSizeXL = false;

    @Input()
    get euiSize2XL() { return this._euiSize2XL; }
    set euiSize2XL(value) { this._euiSize2XL = coerceBooleanProperty(value); }
    _euiSize2XL = false;

    @Input()
    get euiSize3XL() { return this._euiSize3XL; }
    set euiSize3XL(value) { this._euiSize3XL = coerceBooleanProperty(value); }
    _euiSize3XL = false;

    @Input()
    get euiSize4XL() { return this._euiSize4XL; }
    set euiSize4XL(value) { this._euiSize4XL = coerceBooleanProperty(value); }
    _euiSize4XL = false;

    @Input()
    get euiSize5XL() { return this._euiSize5XL; }
    set euiSize5XL(value) { this._euiSize5XL = coerceBooleanProperty(value); }
    _euiSize5XL = false;

    // EXTRA SHAPES
    @Input()
    get euiRounded() { return this._euiRounded; }
    set euiRounded(value) { this._euiRounded = coerceBooleanProperty(value); }
    _euiRounded = false;

    @Input()
    get euiOutline() { return this._euiOutline; }
    set euiOutline(value) { this._euiOutline = coerceBooleanProperty(value); }
    _euiOutline = false;

    @Input()
    get euiCompact() { return this._euiCompact; }
    set euiCompact(value) { this._euiCompact = coerceBooleanProperty(value); }
    _euiCompact = false;

    // STATES
    @Input()
    get euiDisabled() { return this._euiDisabled; }
    set euiDisabled(value) { this._euiDisabled = coerceBooleanProperty(value); }
    _euiDisabled = false;

    @Input()
    get euiResponsive() { return this._euiResponsive; }
    set euiResponsive(value) { this._euiResponsive = coerceBooleanProperty(value); }
    _euiResponsive = false;

    @Input()
    get euiHighlighted() { return this._euiHighlighted; }
    set euiHighlighted(value) { this._euiHighlighted = coerceBooleanProperty(value); }
    _euiHighlighted = false;

    getCssClasses(rootClass: string) {
        return [
            rootClass,
            this._euiPrimary ? `${rootClass}--primary` : '',
            this._euiSecondary ? `${rootClass}--secondary` : ``,
            this._euiInfo ? `${rootClass}--info` : ``,
            this._euiSuccess ? `${rootClass}--success` : ``,
            this._euiWarning ? `${rootClass}--warning` : ``,
            this._euiDanger ? `${rootClass}--danger` : ``,
            this._euiAccent ? `${rootClass}--accent` : ``,
            this._euiSize2XS ? `${rootClass}--size-2xs` : ``,
            this._euiSizeXS ? `${rootClass}--size-xs` : ``,
            this._euiSizeS ? `${rootClass}--size-s` : ``,
            this._euiSizeM ? `${rootClass}--size-m` : ``,
            this._euiSizeL ? `${rootClass}--size-l` : ``,
            this._euiSizeXL ? `${rootClass}--size-xl` : ``,
            this._euiSize2XL ? `${rootClass}--size-2xl` : ``,
            this._euiSize3XL ? `${rootClass}--size-3xl` : ``,
            this._euiSize4XL ? `${rootClass}--size-4xl` : ``,
            this._euiSize5XL ? `${rootClass}--size-5xl` : ``,
            this._euiRounded ? `${rootClass}--rounded` : ``,
            this._euiOutline ? `${rootClass}--outline` : ``,
            this._euiDisabled ? `${rootClass}--disabled disabled` : ``,
            this._euiCompact ? `${rootClass}--compact` : ``,
            this._euiResponsive ? `${rootClass}--responsive` : ``,
            this._euiHighlighted ? `${rootClass}--highlighted` : ``,

            this.styleClass ? this.styleClass : ``,
        ].join(' ').trim();
    }
}
